// SIMO-SELF-KNOWLEDGE-UI-1: UI-Typen + pure Erkennung der strukturierten
// Self-Knowledge-/Error-Payloads, die der Server liefert (Tool-Ergebnisse /
// Fehler-Antworten). Reines, framework-freies Modul → unter Vitest testbar
// ohne DOM.

export type PermissionMode = "off" | "draft" | "suggest" | "auto";

export interface PermissionDeniedPayload {
  error: "permission_denied";
  permission_key: string;
  current_mode: PermissionMode;
  default_mode: PermissionMode;
  available_modes: PermissionMode[];
  human_label: string;
  human_description: string;
  settings_url: string;
  fix_hint: string;
}

export interface ArmedGateBlockedPayload {
  error: "armed_gate_blocked";
  reason: "not_armed" | "armed_expired";
  permission_key: string;
  human_label: string;
  arm_permission_key: string;
  arm_permission_mode: PermissionMode;
  command_center_url: string;
  fix_hint: string;
}

export interface WhoamiPayload {
  runtime_host: string;
  network: string | null;
  commit: string | null;
  branch: string | null;
  source_path: string;
  agent_runtime: string;
  memory_count: number;
  audit_count: number;
}

export interface PermissionListItem {
  id: string;
  connector: string;
  kind: string;
  label: string;
  description: string;
  default_mode: string;
  current_mode: string;
  allowed: boolean;
}

export interface PermissionListPayload {
  count: number;
  settings_url: string;
  permissions: PermissionListItem[];
}

export interface AuditRecentEntry {
  id: number;
  timestamp: string;
  actor: string;
  actionId?: string | null;
  result: string;
  summary: string;
  connector?: string | null;
}

export interface AuditRecentPayload {
  count: number;
  scanned: number;
  entries: AuditRecentEntry[];
}

/** VPS-/Worktree-Fehler mit reason_code (Worktree zusätzlich mit cleanup-Feldern). */
export interface ReasonCodeErrorPayload {
  error: string;
  reason_code: string;
  cleanup_hint?: string;
  current_count?: number;
  max?: number;
  oldest_worktree_id?: string | null;
}

export type SelfKnowledgePayload =
  | { kind: "permission_denied"; data: PermissionDeniedPayload }
  | { kind: "armed_gate_blocked"; data: ArmedGateBlockedPayload }
  | { kind: "whoami"; data: WhoamiPayload }
  | { kind: "permission_list"; data: PermissionListPayload }
  | { kind: "audit_recent"; data: AuditRecentPayload }
  | { kind: "reason_code_error"; data: ReasonCodeErrorPayload };

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Versucht, einen JSON-String zu parsen; gibt sonst den Originalwert zurück. */
export function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

/**
 * Klassifiziert einen (ggf. JSON-stringifizierten) Wert in einen bekannten
 * Self-Knowledge-/Error-Payload. Reihenfolge: spezifische discriminated unions
 * zuerst, generischer reason_code-Fehler zuletzt. `null`, wenn nichts passt.
 */
export function detectSelfKnowledgePayload(value: unknown): SelfKnowledgePayload | null {
  const o = parseMaybeJson(value);
  if (!isObject(o)) return null;

  if (o.error === "permission_denied" && typeof o.permission_key === "string") {
    return { kind: "permission_denied", data: o as unknown as PermissionDeniedPayload };
  }
  if (o.error === "armed_gate_blocked" && typeof o.reason === "string") {
    return { kind: "armed_gate_blocked", data: o as unknown as ArmedGateBlockedPayload };
  }
  if (typeof o.runtime_host === "string" && typeof o.agent_runtime === "string") {
    return { kind: "whoami", data: o as unknown as WhoamiPayload };
  }
  if (Array.isArray(o.permissions) && typeof o.settings_url === "string") {
    return { kind: "permission_list", data: o as unknown as PermissionListPayload };
  }
  if (Array.isArray(o.entries) && typeof o.scanned === "number") {
    return { kind: "audit_recent", data: o as unknown as AuditRecentPayload };
  }
  if (typeof o.error === "string" && typeof o.reason_code === "string") {
    return { kind: "reason_code_error", data: o as unknown as ReasonCodeErrorPayload };
  }
  return null;
}

/**
 * Extrahiert einen Payload aus einem Fehler-String der Form
 * `"403 Forbidden: {json}"` (so wirft der api.ts-`jfetch`). Sucht den ersten
 * `{`/`[` und versucht, ab dort zu parsen+erkennen. `null`, wenn kein
 * struktureller Payload enthalten ist (dann bleibt der rohe Fehlertext).
 */
export function extractPayloadFromErrorString(message: string | null | undefined): SelfKnowledgePayload | null {
  if (!message) return null;
  const direct = detectSelfKnowledgePayload(message);
  if (direct) return direct;
  const braceIdx = message.indexOf("{");
  if (braceIdx >= 0) {
    const candidate = message.slice(braceIdx);
    const fromBody = detectSelfKnowledgePayload(candidate);
    if (fromBody) return fromBody;
  }
  return null;
}
